import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, groupBy, map, mergeMap, take, tap } from 'rxjs/operators';

import { ApplicationHttpClient } from '../components/shared/http/application-http-client';
import { Routes } from '../config/routes';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { LocalService } from './storage/local.service';

/**
 * SD-6481 — generic UI client for the per-user-per-org UserPreferences API
 * (core module SD-6488). This is the first consumer of the backend store;
 * the patterns here (org-scoped session cache, localStorage write-through,
 * per-key debounced PUT, logout flush) are what filters/sort (SD-7319
 * Phase 2) adopt later.
 *
 * Storage model:
 *   - In-memory `Map<key, value>` is the synchronous read source so tables
 *     render instantly with no spinner.
 *   - `loadAll()` warms the cache from the backend on entering the
 *     authenticated shell (single bulk GET).
 *   - The cache is mirrored to localStorage (`payhoa-user-prefs-{orgId}`)
 *     so the first paint after a hard reload — before the bulk GET resolves
 *     — still has the user's last-known values.
 *   - Writes update the cache + mirror synchronously and debounce the
 *     backend PUT (rapid toggles collapse into one call). A pending write
 *     survives navigation because this service is a root singleton, and is
 *     flushed on logout.
 *
 * The backend is preference-type agnostic — value shapes are owned by the
 * consumer. Keys follow `<concept>.<id>` (e.g. `columns.payments`).
 */
@Injectable({
    providedIn: 'root',
})
export class UserPreferencesApiService
{
    private static readonly MIRROR_DOMAIN = 'payhoa-user-prefs';

    private static readonly DEBOUNCE_MS = 400;

    private _http: ApplicationHttpClient;

    /** Org the cache currently holds values for — reset on org switch. */
    private _cacheOrgId: number | null = null;

    private _cache = new Map<string, unknown>();

    private _cache$ = new BehaviorSubject<ReadonlyMap<string, unknown>>(this._cache);

    /** Values written but not yet PUT, keyed by preference key. */
    private _pending = new Map<string, unknown>();

    /**
     * Keys the user has set or removed this session. A bulk GET (`loadAll`)
     * that started before the change must not clobber it with the stale
     * server snapshot when it resolves — these keys win over the snapshot.
     * Cleared on org switch.
     */
    private _locallyTouched = new Set<string>();

    /** Emits a preference key whose debounced PUT should be (re)scheduled. */
    private _debounce$ = new Subject<string>();

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;

        // Per-key debounce: each key gets its own debounce window so a burst
        // of toggles on one table collapses into a single PUT without
        // delaying writes to a different key.
        this._debounce$.pipe(
            groupBy((key) => key),
            mergeMap((group) => group.pipe(debounceTime(UserPreferencesApiService.DEBOUNCE_MS))),
        ).subscribe((key) => this._flushKey(key));
    }

    /**
     * Warm the session cache with a single bulk GET. Called on entering the
     * authenticated shell. Never throws into the caller — a failed prefs
     * fetch must not break the app shell; the localStorage mirror still
     * serves the last-known values.
     */
    loadAll(): Observable<void> {
        const orgId = this._orgId();
        if (orgId === null) {
            return of(void 0);
        }

        this._ensureOrg(orgId);

        const url = Routes.MakeLegFiCoreUrl(Routes.UserPreferences.Collection(orgId));

        return this._http.get<Record<string, unknown>>(url).pipe(
            tap((preferences) => this._replaceCache(orgId, preferences ?? {})),
            map(() => void 0),
            catchError(() => of(void 0)),
        );
    }

    /** Synchronous read from the session cache (null when unset). */
    get<T>(key: string): T | null {
        const orgId = this._orgId();
        if (orgId === null) {
            return null;
        }

        this._ensureOrg(orgId);

        return (this._cache.has(key) ? this._cache.get(key) : null) as T | null;
    }

    /**
     * Reactive read — emits the current cached value and re-emits when it
     * changes (e.g. when `loadAll()` resolves on a fresh device). Lets a
     * consumer render instantly from cache and self-correct once the backend
     * value arrives.
     */
    watch<T>(key: string): Observable<T | null> {
        return this._cache$.pipe(
            map((cache) => (cache.has(key) ? cache.get(key) : null) as T | null),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        );
    }

    /** Write-through + immediate PUT (use for deliberate single actions). */
    set<T>(key: string, value: T): void {
        // Record the touch AFTER the write: _writeLocal()/_ensureOrg() can
        // reset _locallyTouched while initializing the org, which would
        // otherwise wipe the entry we just added (and let a concurrent
        // loadAll() clobber this write).
        this._writeLocal(key, value);
        this._locallyTouched.add(key);
        this._pending.delete(key);
        this._put(key, value);
    }

    /** Write-through + debounced PUT (use for rapid/incremental changes). */
    setDebounced<T>(key: string, value: T): void {
        this._writeLocal(key, value);
        this._locallyTouched.add(key);
        this._pending.set(key, value);
        this._debounce$.next(key);
    }

    /**
     * Write-through to the session cache + localStorage mirror with NO network
     * write. Stages the value as pending so a later {@link flushKey} (or
     * {@link flushPending} on logout) persists it. Use when many incremental
     * edits should collapse into a single backend write at a natural commit
     * point — e.g. a column picker that persists once on close rather than on
     * every checkbox toggle.
     */
    setLocal<T>(key: string, value: T): void {
        // Record the touch AFTER the write (see set() — _ensureOrg can reset
        // _locallyTouched while initializing the org), so a concurrent
        // loadAll() can't clobber the staged value.
        this._writeLocal(key, value);
        this._locallyTouched.add(key);
        this._pending.set(key, value);
    }

    /** Clear the override locally and on the backend (reverts to defaults). */
    remove(key: string): void {
        // Cancel any queued debounced PUT so it can't resurrect the value
        // after the DELETE lands.
        this._pending.delete(key);
        this._deleteLocal(key);
        // Record the touch AFTER the delete (see set() — _ensureOrg can reset
        // _locallyTouched) so a concurrent loadAll() can't resurrect the key.
        this._locallyTouched.add(key);

        const orgId = this._orgId();
        if (orgId === null) {
            return;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.UserPreferences.Item(orgId, key));
        this._http.delete(url).pipe(take(1)).subscribe();
    }

    /**
     * Persist a single staged key to the backend immediately. No-op when the
     * key has nothing pending. Pairs with {@link setLocal} to commit a batch
     * of local edits at a natural boundary (e.g. a picker closing).
     */
    flushKey(key: string): void {
        this._flushKey(key);
    }

    /**
     * Fire every pending debounced PUT immediately. Called on logout, before
     * the JWT is cleared, so the org context is still present when each URL
     * is built.
     */
    flushPending(): void {
        for (const key of Array.from(this._pending.keys())) {
            this._flushKey(key);
        }
    }

    private _orgId(): number | null {
        return LegFiJwtService.read()?.orgId ?? null;
    }

    /** Reset + reseed the cache when the active org changes. */
    private _ensureOrg(orgId: number): void {
        if (this._cacheOrgId === orgId) {
            return;
        }

        this._cacheOrgId = orgId;
        this._cache = new Map<string, unknown>();
        this._locallyTouched.clear();
        this._seedFromMirror(orgId);
        this._cache$.next(this._cache);
    }

    private _replaceCache(orgId: number, preferences: Record<string, unknown>): void {
        // Guard against a bulk GET that resolves after the active org changed
        // — applying its (other-org) snapshot would corrupt the cache.
        if (orgId !== this._orgId()) {
            return;
        }

        const next = new Map<string, unknown>(Object.entries(preferences));

        // Preserve any changes the user made locally this session: a write
        // still holds its value in the current cache (it wins over the
        // snapshot), and a removal is absent (so we drop it from the snapshot).
        // Without this, an in-flight bulk GET started before the edit would
        // clobber it and the table would visibly revert.
        for (const key of this._locallyTouched) {
            if (this._cache.has(key)) {
                next.set(key, this._cache.get(key));
            } else {
                next.delete(key);
            }
        }

        this._cacheOrgId = orgId;
        this._cache = next;
        this._persistMirror(orgId);
        this._cache$.next(this._cache);
    }

    private _writeLocal<T>(key: string, value: T): void {
        const orgId = this._orgId();
        if (orgId === null) {
            return;
        }

        this._ensureOrg(orgId);
        this._cache.set(key, value);
        this._persistMirror(orgId);
        this._cache$.next(this._cache);
    }

    private _deleteLocal(key: string): void {
        const orgId = this._orgId();
        if (orgId === null) {
            return;
        }

        this._ensureOrg(orgId);
        this._cache.delete(key);
        this._persistMirror(orgId);
        this._cache$.next(this._cache);
    }

    private _flushKey(key: string): void {
        if (!this._pending.has(key)) {
            return;
        }

        const value = this._pending.get(key);
        this._pending.delete(key);
        this._put(key, value);
    }

    private _put<T>(key: string, value: T): void {
        const orgId = this._orgId();
        if (orgId === null) {
            return;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.UserPreferences.Item(orgId, key));
        this._http.put(url, JSON.stringify({ value })).pipe(take(1)).subscribe();
    }

    private _mirrorKey(orgId: number): string {
        return `${UserPreferencesApiService.MIRROR_DOMAIN}-${orgId}`;
    }

    private _persistMirror(orgId: number): void {
        const key = this._mirrorKey(orgId);

        if (this._cache.size === 0) {
            LocalService.destroy(key);

            return;
        }

        LocalService.set(key, JSON.stringify(Object.fromEntries(this._cache)));
    }

    private _seedFromMirror(orgId: number): void {
        const raw = LocalService.get(this._mirrorKey(orgId));
        if (raw === null || raw === '') {
            return;
        }

        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            for (const [key, value] of Object.entries(parsed)) {
                this._cache.set(key, value);
            }
        } catch {
            // Corrupt mirror — drop it so we don't trip on it again.
            LocalService.destroy(this._mirrorKey(orgId));
        }
    }
}
